import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { DocumentStatus } from '@/schema'

import { TflMeta } from '../types.ts'

type ActiveTab = 'TOC' | 'COMMENTS' | 'ACTIVITY'

interface TflViewerStore {
  // State
  commentDialogOpen: boolean
  transferDialogOpen: boolean
  historyDrawerOpen: boolean
  lastHtmlByFileId: Record<string, string>
  metadataByDocId: Record<string, Partial<TflMeta>>
  activeFileId?: string
  activeTab: ActiveTab
  selectedStatusFilters: DocumentStatus[]
  isSidebarCollapsed: boolean
  isRightSidebarCollapsed: boolean
  documentVersionMap: Record<string, string[]>
  versionNumberByDocId: Record<string, number>
  pendingReviewersByDocId: Record<string, number>
  reviewerDetailsDialogOpen: boolean

  // Actions
  setCommentDialogOpen: (open: boolean) => void
  setTransferDialogOpen: (open: boolean) => void
  setHistoryDrawerOpen: (open: boolean) => void
  setHtmlSnapshot: (fileId: string, html: string) => void
  setDocumentMetadata: (
    docId: string,
    metadata: Partial<TflMeta> | undefined
  ) => void
  removeDocumentMetadata: (docIds: string[]) => void
  setActiveFileId: (fileId?: string) => void
  setActiveTab: (tab: ActiveTab) => void
  setStatusFilters: (filters: DocumentStatus[]) => void
  toggleStatusFilter: (status: DocumentStatus) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setRightSidebarCollapsed: (collapsed: boolean) => void
  setDocumentVersionMap: (maps: {
    documentVersionMap: Record<string, string[]>
    versionNumberByDocId: Record<string, number>
  }) => void
  getDocumentIdByVersion: (
    displayName: string,
    versionNumber: number
  ) => string | undefined
  setPendingReviewersCount: (documentId: string, count: number) => void
  getPendingReviewersCount: (documentId: string) => number
  setReviewerDetailsDialogOpen: (open: boolean) => void
  toggleCollapseBreakpoint: (collapsed: boolean) => void
  reset: () => void
}

const initialState = {
  commentDialogOpen: false,
  transferDialogOpen: false,
  historyDrawerOpen: false,
  lastHtmlByFileId: {},
  metadataByDocId: {},
  activeFileId: undefined,
  activeTab: 'TOC' as const,
  selectedStatusFilters: [],
  isSidebarCollapsed: false,
  isRightSidebarCollapsed: false,
  documentVersionMap: {},
  versionNumberByDocId: {},
  pendingReviewersByDocId: {},
  reviewerDetailsDialogOpen: false,
}

export const useTflViewerStore = create<TflViewerStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setCommentDialogOpen: (open: boolean) => {
        set({ commentDialogOpen: open }, false, 'setCommentDialogOpen')
      },
      setTransferDialogOpen: (open: boolean) => {
        set({ transferDialogOpen: open }, false, 'setTransferDialogOpen')
      },
      setHistoryDrawerOpen: (open: boolean) => {
        set({ historyDrawerOpen: open }, false, 'setHistoryDrawerOpen')
      },

      setHtmlSnapshot: (fileId: string, html: string) => {
        set(
          (state) => ({
            lastHtmlByFileId: {
              ...state.lastHtmlByFileId,
              [fileId]: html,
            },
          }),
          false,
          'setHtmlSnapshot'
        )
      },

      setDocumentMetadata: (docId: string, metadata: Partial<TflMeta>) => {
        set(
          (state) => {
            const isExisting = Object.keys(state.metadataByDocId).includes(
              docId
            )
            if (isExisting && state.metadataByDocId[docId] === undefined)
              return state
            return {
              metadataByDocId: {
                ...state.metadataByDocId,
                [docId]: { ...state.metadataByDocId[docId], ...metadata },
              },
            }
          },
          false,
          'setDocumentMetadata'
        )
      },

      /**
       * Remove document metadata from the store
       * @param docIds - The document IDs to remove
       */
      removeDocumentMetadata: (docIds: string[]) => {
        set(
          (state) => ({
            metadataByDocId: Object.fromEntries(
              Object.entries(state.metadataByDocId).filter(
                ([id]) => !docIds.includes(id)
              )
            ),
          }),
          false,
          'removeDocumentMetadata'
        )
      },

      setActiveFileId: (fileId?: string) => {
        set({ activeFileId: fileId }, false, 'setActiveFileId')
      },

      setActiveTab: (tab: 'TOC' | 'COMMENTS' | 'ACTIVITY') => {
        set({ activeTab: tab }, false, 'setActiveTab')
      },

      setStatusFilters: (filters: DocumentStatus[]) => {
        set({ selectedStatusFilters: filters }, false, 'setStatusFilters')
      },

      toggleStatusFilter: (status: DocumentStatus) => {
        set(
          (state) => {
            const isSelected = state.selectedStatusFilters.includes(status)
            return {
              selectedStatusFilters: isSelected
                ? state.selectedStatusFilters.filter((s) => s !== status)
                : [...state.selectedStatusFilters, status],
            }
          },
          false,
          'toggleStatusFilter'
        )
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ isSidebarCollapsed: collapsed }, false, 'setSidebarCollapsed')
      },

      setRightSidebarCollapsed: (collapsed: boolean) => {
        set(
          { isRightSidebarCollapsed: collapsed },
          false,
          'setRightSidebarCollapsed'
        )
      },

      setDocumentVersionMap: (maps: {
        documentVersionMap: Record<string, string[]>
        versionNumberByDocId: Record<string, number>
      }) => {
        set(
          {
            documentVersionMap: maps.documentVersionMap,
            versionNumberByDocId: maps.versionNumberByDocId,
          },
          false,
          'setDocumentVersionMap'
        )
      },

      getDocumentIdByVersion: (displayName: string, versionNumber: number) => {
        const state = get()
        const documentIds = state.documentVersionMap[displayName]

        if (!documentIds || documentIds.length === 0) {
          return undefined
        }

        return documentIds.find(
          (docId) => state.versionNumberByDocId[docId] === versionNumber
        )
      },

      setPendingReviewersCount: (documentId: string, count: number) => {
        set(
          (state) => ({
            pendingReviewersByDocId: {
              ...state.pendingReviewersByDocId,
              [documentId]: count,
            },
          }),
          false,
          'setPendingReviewersCount'
        )
      },

      getPendingReviewersCount: (documentId: string) => {
        const state = get()
        return state.pendingReviewersByDocId[documentId] ?? 0
      },

      setReviewerDetailsDialogOpen: (open: boolean) => {
        set(
          { reviewerDetailsDialogOpen: open },
          false,
          'setReviewerDetailsDialogOpen'
        )
      },

      toggleCollapseBreakpoint: (collapsed: boolean) => {
        set(
          { isSidebarCollapsed: collapsed, isRightSidebarCollapsed: collapsed },
          false,
          'toggleCollapseBreakpoint'
        )
      },

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    {
      name: 'TflViewerStore',
    }
  )
)
